/* ltt-card-add — adding from a product card does not fling the cart open.

   THE RULE (Hamdi, 2026-09-09): tapping the bag on a card puts the product in
   the cart and leaves the shopper where she is. The side cart opens by itself
   only once the cart holds `openAt` items — 4, which is exactly where the
   Buy-3-Get-1 offer completes, so the one time it interrupts her is the one
   time it has something to show. The cart icon always opens it, as before.

   SCOPE IS THE CARD, AND ONLY THE CARD. The product page and the quick-view
   popup still open the drawer on every add: there the shopper has arrived at a
   surface about ONE product and pressed a full-width Add to cart, and the
   drawer is the confirmation that the press worked. A card tap is a passing
   gesture in the middle of browsing, which is what makes the interruption
   wrong there and right here.

   WHY THE ADD IS NOT SILENT-SILENT: with the drawer suppressed, the only proof
   anything happened would be the header count changing by one, which nobody
   watches. So a toast says it, from the theme locales.

   ── HOW THE SUPPRESSION WORKS ──────────────────────────────────────────────
   Dawn's CartDrawer.renderContents() repaints the sections AND opens the
   drawer, in that order, and product-form.js calls it for every add from every
   surface. There is no flag to pass and no event fired in between.

   So renderContents is wrapped. When the pending add came from a card, `open`
   is shadowed with a no-op OWN property on the drawer element for the duration
   of the call, then restored.

   🪤 renderContents does not open synchronously — it opens inside a
   `setTimeout(…)` it queues itself. The restore therefore has to be queued
   AFTER that one, not run at the end of the call, or the real `open` is back
   in place by the time Dawn reaches for it. Same-delay timers fire in the
   order they were queued, so a `setTimeout(…, 0)` queued right after Dawn's is
   guaranteed to run second. Do not "simplify" this into a direct restore.

   🪤 THE ORIGIN FLAG IS A DEADLINE, NOT A COUNTER. A counter incremented at
   submit and decremented at render leaks the moment a submit does not reach a
   render — product-form.js returns early on a double click while
   `aria-disabled` is set, and ltt-qty-cap.js kills the event outright when the
   per-product ceiling is hit. A leaked counter would silence the drawer
   permanently on that page. A deadline expires on its own, so the worst case is
   one product-page add inside a ten-second window not opening the drawer, and
   the page heals itself. */
(function () {
  'use strict';
  if (window.lttCardAddBound) return;
  window.lttCardAddBound = true;

  var CardAdd = customElements.get('cart-drawer');
  /* No drawer on this store's settings (cart_type is 'page' or 'notification')
     — there is nothing to suppress and nothing to open. */
  if (!CardAdd) return;

  var WINDOW_MS = 10000;

  /* Read fresh every time: the node is replaced wholesale by the Section
     Rendering API on each cart change, so a cached reference goes stale on the
     first add. */
  function cartState() {
    var node = document.querySelector('[data-ltt-cart-qty]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function itemCount(data) {
    if (!data || !data.lines) return 0;
    var total = 0;
    for (var i = 0; i < data.lines.length; i++) total += data.lines[i][1];
    return total;
  }

  /* CAPTURE phase, registered after ltt-qty-cap.js — and both of those are
     load-bearing.

     🪤 THE BUBBLE PHASE DOES NOT WORK HERE, and it fails in the direction that
     looks like nothing is wrong: product-form.js binds its own submit handler
     on the FORM and calls preventDefault() on EVERY add, cards included. A
     document-level bubble listener therefore always sees defaultPrevented, and
     a `if (event.defaultPrevented) return` guard — which reads as "skip refused
     adds" — silently skips every add there is. Measured on this draft: the item
     landed in the cart, the drawer opened anyway, no toast.

     Capture runs before the form's own handler, so nothing has cancelled
     anything yet. Refused adds are still skipped, but by ORDER rather than by a
     flag: ltt-qty-cap.js registers its capture listener first (the layout loads
     it first, and same-node same-phase listeners fire in registration order)
     and kills the event with stopImmediatePropagation(), so this listener is
     never reached for an add that is not going to happen. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form) return;
      var host = form.closest('[data-ltt-card-add]');
      if (!host) return;
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.__lttCardAddUntil = Date.now() + WINDOW_MS;
    },
    true
  );

  var render = CardAdd.prototype.renderContents;

  CardAdd.prototype.renderContents = function (parsedState) {
    var fromCard = Date.now() < (this.__lttCardAddUntil || 0);
    this.__lttCardAddUntil = 0;

    if (!fromCard) return render.call(this, parsedState);

    var drawer = this;
    var realOpen = CardAdd.prototype.open;
    /* An own property shadowing the prototype method, so the prototype itself
       is never mutated and a second drawer instance (there is only ever one,
       but still) is unaffected. */
    this.open = function () {};

    render.call(this, parsedState);

    setTimeout(function () {
      delete drawer.open;

      var data = cartState();
      var openAt = data && data.openAt > 0 ? data.openAt : 0;

      if (openAt && itemCount(data) >= openAt) {
        realOpen.call(drawer);
        return;
      }

      /* Below the threshold the only sign anything happened would be the
         header count ticking over, so say it out loud. The words ride in the
         freshly repainted JSON, never in a string in this file — the English
         store once answered its shoppers in Arabic exactly that way. */
      if (data && data.added && typeof window.lttToast === 'function') {
        window.lttToast(data.added);
      }
    }, 0);
  };
})();
